import { ProFormCaptcha, ProForm } from '@ant-design/pro-form';
import { TdsFooter } from '@tencent/tds-ui/src/tds-footer';
import { trpc } from '@tencent/tpro_rmonitor_product_manager_user';
import { Form, Button, Input, AutoComplete } from 'antd';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HelperQRcodeIcon from '@/assets/images/index/1x/qrcode-img1x.png';
import BuglyBg from '@/assets/images/login/bugly-pro-login.svg';
import SuccessIcon from '@/assets/images/resource-pack/success-max.react.svg?react';
import { ENV } from '@/infrastructure/env-config';
import { ModalIconType, ModalMsg, ModalType } from '@/presentation/components/modal-msg';
import { INTERNET_JOBS } from '@/presentation/pages/resource-pack/consult/config';
import styles from '@/presentation/pages/resource-pack/consult/index.module.scss';
import { userService } from '@/service/pro-user';
import { isIOTPhone, isPhone } from '@raft/utils/src/validator';
import { Header } from '../../main-pro/header';

import ISendVerifyCodeReq = trpc.rmonitor.product_manager.ISendVerifyCodeReq;
import CodeType = trpc.rmonitor.product_manager.CodeType;

const FormItem = ProForm.Item;
const { TextArea } = Input;
interface IForm {
  name: string;
  verifyCode: string;
  phone: string;
  company: string;
  wechatId: string;
  email?: string;
  helpContent: string;
  position?: string;
}

const ILLEGAL_REG = /^[\u4e00-\u9fa5a-zA-Z0-9=+/@#_-]+$/;

const Consult: FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<IForm>();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      ModalMsg({
        type: ModalType.CONFIRM,
        typeIcon: ModalIconType.INFO,
        content: t('确认提交吗？'),
        cancelText: t('取消'),
        title: t('商务咨询'),
        okText: t('确认'),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onOk: () => new Promise<void>(async (resolve, reject) => {
          const verifyStatus = await userService.verifyCode({
            type: CodeType.PHONE,
            code: values.verifyCode,
            number: values.phone,
          });
          if (verifyStatus) {
            const res = await userService.collectUserInfo({
              collectUserInfo: {
                name: values.name,
                company: values.company,
                wechatId: values.wechatId,
                email: values.email,
                phone: values.phone,
                helpContent: values.helpContent,
                position: values.position,
              },
            });
            if (res) {
              resolve();
              setIsSubmit(true);
            } else {
              reject();
            }
          } else {
            reject();
          }
        }),
      });
    } catch (e) {
    }
  };
  const sendVerifyCode = async (req: ISendVerifyCodeReq): Promise<void> => {
    const res = await userService.sendVerifyCode(req);
    if (!res) {
      throw new Error('');
    }
  };
  const verifyPhone = (_: unknown, value: string, callback: (msg?: string) => void) => {
    if (isIOTPhone(value)) {
      return callback(t('不能使用物联网码号'));
    }
    return isPhone(value) || !value ? callback() : callback(t('手机号码格式不正确'));
  };

  if (isSubmit) {
    return <div className={styles.successPage}>
        <SuccessIcon/>
        <p className={styles.submitTxt}>{t('您的申请已提交，后续会有工作人员与您联系')}</p>
    </div>;
  }

  return (
    <div>
      <Header style={{ background: '#fff' }} />
      <div className={styles.container}>
        <div className={styles.logoBg} style={{
          backgroundImage: `url(${BuglyBg})`,
        }}></div>
        <div className={styles.page}>
          <div className={styles.rightContainer}>
            <div className={styles.consult}>
              <div className={styles.header}>
                <p className={styles.title}>{t('商务咨询')}</p>
                <span className={styles.subTitle}>{t('提交反馈问题后，我们会有专员联系您进行后续的解答')}</span>
              </div>
              <div className={styles.content}>
                <ProForm<IForm> layout={'vertical'} form={form} submitter={false}>
                  <FormItem name={'name'} label={t('姓名')} rules={[
                    { required: true, message: t('请输入您的姓名') },
                    { pattern: ILLEGAL_REG, message: t('姓名中包含非法字符') },
                  ]}>
                    <Input placeholder={t('请输入仅包含中文字符、英文字母、数字以及特殊字符（=、+、/、@、#、_、-）的字符串')}/>
                  </FormItem>
                  <FormItem className={styles.phone} name={'phone'} label={t('手机号')} rules={[{
                    required: true, message: t('请输入您的手机号'),
                  },
                  {
                    validator: verifyPhone,
                  },
                  ]}>
                    <Input placeholder={t('请输入您的手机号')}/>
                  </FormItem>
                  <FormItem required shouldUpdate className={styles.verifyCode}>
                    {(form) => {
                      const fieldValue = form.getFieldValue('phone');
                      const hasError = form.getFieldError('verifyCode');
                      return (
                        <ProFormCaptcha
                          phoneName={'phone'}
                          name={'verifyCode'}
                          placeholder={t('请输入验证码')}
                          rules={[
                            { required: true, message: t('请输入验证码') },
                          ]}
                          fieldProps={{
                            className: styles.captchaInput,
                          }}
                          captchaProps={{
                            className: classNames({
                              [styles.captchaBtn]: true,
                              [styles.captchaBtnWithError]: !isEmpty(hasError),
                            }),
                            disabled: isIOTPhone(fieldValue) || !isPhone(fieldValue),
                            loading: false,
                          }}
                          countDown={60}
                          onGetCaptcha={() => sendVerifyCode({
                            type: CodeType.PHONE,
                            number: fieldValue,
                          })}
                        />
                      );
                    }}
                  </FormItem>
                  <FormItem name={'company'} label={t('公司')} rules={[
                    { required: true, message: t('请输入公司名称') },
                    { pattern: ILLEGAL_REG, message: t('公司名称中包含非法字符') },
                  ]}>
                    <Input placeholder={t('请输入公司名称')} />
                  </FormItem>
                  <FormItem name={'wechatId'} label={t('微信号')} rules={[
                    { required: true, message: t('请输入您的微信号') },
                    { pattern: ILLEGAL_REG, message: t('微信号中包含非法字符') },
                  ]}>
                    <Input placeholder={t('请输入您的微信号')}/>
                  </FormItem>
                  <FormItem name={'position'} label={t('职务')}>
                    <AutoComplete options={INTERNET_JOBS} placeholder={t('请输入您的职务')}/>
                  </FormItem>
                  <FormItem name={'email'} label={t('邮箱')}>
                    <Input placeholder={t('请输入您的公司邮箱')}/>
                  </FormItem>
                  <FormItem name={'helpContent'} label={t('希望得到的帮助')} rules={[
                    { required: true, message: t('请输入您希望得到的帮助') },
                    { pattern: ILLEGAL_REG, message: t('内容中包含非法字符') },
                  ]}>
                    <TextArea placeholder={t('请输入仅包含中文字符、英文字母、数字以及特殊字符（=、+、/、@、#、_、-）的字符串')} autoSize={{ maxRows: 5, minRows: 2 }}/>
                  </FormItem>
                </ProForm>
                <Button onClick={onSubmit} type={'primary'} className={styles.button}>{t('提交')}</Button>
              </div>
            </div>
            <div className={styles.qrCode}>
              <div className={styles.helperQrcodeContainer}>
                <img src={HelperQRcodeIcon} className={styles.helperQrcode} alt={t('二维码')}/>
              </div>
              <span className={'text-[#fff]'}>{t('扫码沟通更方便')}</span>
            </div>
          </div>
          <TdsFooter
            isWoa={false}
            privacyPolicyUrl={ENV.isGlobal ? 'https://bugly.tds.tencent.com/docs/other/Bugly_Privacy_Policy' : undefined}
          />
        </div>

      </div>
    </div>
  );
}
;

export default Consult;
